import React, { useState } from "react";
import { useSelector } from "react-redux";
import { MdClose } from "react-icons/md";
import { UrgencyIcon, Highlight } from "./AlertArt";

const longDate = (value) =>
  new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

/** The banner itself, separate from where its data comes from. */
export const GraceBannerView = ({ graceEndsOn, canRenew, onRenew, onClose }) => (
  <div
    className="flex flex-wrap items-center gap-3 px-4 py-2.5 md:px-6"
    style={{ background: "#FDEEEE", borderBottom: "1px solid #F6D4D4" }}
    role="alert"
  >
    <span className="shrink-0">
      <UrgencyIcon kind="time" color="#C94B4B" size={22} />
    </span>
    <p className="min-w-0 flex-1 text-[14px]" style={{ color: "#2F3A45" }}>
      <strong>Subscription expired.</strong> You are in a grace period
      {graceEndsOn ? (
        <>
          {" "}— the account will be paused after{" "}
          <Highlight color="#C94B4B" bg="#FADADA">
            {longDate(graceEndsOn)}
          </Highlight>
          .
        </>
      ) : (
        " and the account will be paused shortly."
      )}{" "}
      {canRenew
        ? "Renew now to avoid interruption."
        : "Please ask your clinic owner to renew."}
    </p>
    {canRenew && (
      <button
        type="button"
        onClick={onRenew}
        className="shrink-0 rounded-lg px-4 py-1.5 text-[13px] font-semibold text-white"
        style={{
          background: "linear-gradient(135deg, #0285c7 0%, #0369a1 100%)",
        }}
      >
        Renew now
      </button>
    )}
    {onClose && (
      <button
        type="button"
        aria-label="Dismiss"
        title="Hide until next login"
        onClick={onClose}
        className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
        style={{ color: "#8A6B6B" }}
      >
        <MdClose size={18} />
      </button>
    )}
  </div>
);

/**
 * Stays at the top of every page while a lapsed subscription is inside its
 * grace period. Everyone sees it; the Renew button is for the owner/admin.
 */
const GraceBanner = () => {
  const auth = useSelector((state) => state.auth);

  // Closing it hides it for this sign-in only; the next login shows it again
  // (the account is still in its grace period).
  const key = auth?.token
    ? `simpld.graceBanner.${String(auth.token).slice(-16)}`
    : null;
  const [hidden, setHidden] = useState(() => {
    try {
      return !!key && sessionStorage.getItem(key) === "1";
    } catch (e) {
      return false;
    }
  });

  if (!auth?.in_grace || hidden) return null;

  const canRenew =
    typeof auth.role === "string" &&
    (auth.role.startsWith("Owner") || auth.role === "Admin");

  return (
    <GraceBannerView
      graceEndsOn={auth.grace_ends_on}
      canRenew={canRenew}
      onClose={() => {
        setHidden(true);
        try {
          if (key) sessionStorage.setItem(key, "1");
        } catch (e) {
          /* hidden for this page view only */
        }
      }}
      onRenew={() =>
        window.dispatchEvent(
          new CustomEvent("simpld:open-account-prompt", {
            detail: { view: "renew" },
          })
        )
      }
    />
  );
};

export default GraceBanner;
